import { ICourseContentsNodes } from '@dtos/Course'
import { getVideoThumb } from '@utils/videoThumbs'
import React, { useCallback, useMemo } from 'react'
import { Card, useMedia, isWeb } from 'tamagui'
import {
  isContentAvailable,
  releaseContentDateText,
} from '@utils/contentAvailable'
import { IEnrollment } from '@dtos/Enrollment'
import { router } from 'expo-router'
import { useCurrentLesson } from '@hooks/useCurrentLesson'
import { useScaleAnimation } from '@hooks/useScaleAnimation'
import { useHasFeature } from '@hooks/useThemeParams'
import { MEMBERS_PAGE_COURSE_CONTENTS } from '@constants/features'
import {
  courseContentPath,
  heroThemeCourseContentPath,
} from '@utils/coursePaths'
import { useAdminPreview } from '@hooks/useAdminPreview'
import { EmptyLessonCard } from './EmptyLessonCard'
import { LessonThumbnail } from './LessonThumbnail'
import { LessonFooter } from './LessonFooter'
import { useCourseRouteParams } from '@contexts/CourseRouteParamsContext'

interface ILessonCardProps extends React.ComponentProps<typeof Card> {
  hasContentLesson: boolean
  width: number
  lessonData: ICourseContentsNodes
  enrollment: IEnrollment
  isSliderMoving?: boolean
  moduleId: number
  parentCourseModuleId?: number
  isLessonBlockedByLinearProgress?: boolean
  showTitle?: boolean
}

export const LessonCard = React.memo(function LessonCard({
  hasContentLesson,
  width,
  lessonData,
  enrollment,
  isSliderMoving = false,
  moduleId,
  parentCourseModuleId,
  isLessonBlockedByLinearProgress = false,
  showTitle = true,
  ...rest
}: ILessonCardProps) {
  const { setCurrentLesson } = useCurrentLesson()
  const { courseId } = useCourseRouteParams()
  const hasCourseContentsPage = useHasFeature(MEMBERS_PAGE_COURSE_CONTENTS)
  const { isPreview } = useAdminPreview()
  const { startAnimation, endAnimation, scaleValues } = useScaleAnimation(
    lessonData.id.toString(),
  )
  const media = useMedia()

  const cardMinHeight = media.gtSm ? 300 : 206
  const cardMinHeightStyle = isWeb
    ? `${cardMinHeight}px !important`
    : cardMinHeight

  const dataAvailable = useMemo(
    () => ({
      releaseAt: lessonData.lesson.releaseAt,
      releaseAfter: lessonData.lesson.releaseAfter,
      lockAt: lessonData.lesson.lockAt,
      lockAfter: lessonData.lesson.lockAfter,
      createdAt: enrollment.activatedAt,
      courseClassRelease: enrollment.courseClassRelease,
      courseClassReleaseOffset: lessonData.lesson.courseClassReleaseOffset,
    }),
    [
      lessonData.lesson.releaseAt,
      lessonData.lesson.releaseAfter,
      lessonData.lesson.lockAt,
      lessonData.lesson.lockAfter,
      lessonData.lesson.courseClassReleaseOffset,
      enrollment.activatedAt,
      enrollment.courseClassRelease,
    ],
  )

  const cardWidthStyle = isWeb ? `${width}px !important` : width
  const isLessonAvailable =
    isContentAvailable(dataAvailable) && !isLessonBlockedByLinearProgress
  const releaseDateText = releaseContentDateText(dataAvailable)
  const canAnimateCard = isLessonAvailable && !isSliderMoving
  const isYoutubeOrVimeo = ['Youtube', 'Vimeo'].includes(
    lessonData.lesson.media?.host || '',
  )
  const displayThumbnail = lessonData.lesson.coverImage || isYoutubeOrVimeo
  const lessonIconColor = isLessonAvailable ? 'accent' : 'icon-default'

  const thumbnailHeight = useMemo(() => {
    if (media.gtMd) return 202
    if (media.gtSm) return 164
    return 153
  }, [media.gtMd, media.gtSm])

  const lessonTypeContainerHeight = useMemo(() => {
    if (media.gtMd) return 202
    if (media.gtSm) return 164
    return 144
  }, [media.gtMd, media.gtSm])

  const isLessonCompleted = useCallback(
    (lessonId: number) => {
      return (
        enrollment?.progressCard?.lessonsProgresses?.some(
          (lesson) => lesson.lessonId === lessonId && lesson.completed,
        ) ?? false
      )
    },
    [enrollment?.progressCard?.lessonsProgresses],
  )

  const displayLessonStatus = useMemo(() => {
    return (
      isLessonAvailable === false || isLessonCompleted(lessonData.lesson.id)
    )
  }, [isLessonAvailable, isLessonCompleted, lessonData.lesson.id])

  const lessonStatusConfig = useMemo(
    () => ({
      completed: {
        icon: ['fal', 'check-circle'],
        color: 'progress-complete',
        label: 'Item concluído',
      },
      available: {
        icon: ['fal', 'calendar-alt'],
        color: 'icon-primary',
        label: releaseDateText,
      },
      linearRequirement: {
        icon: ['fal', 'exclamation-circle'],
        color: 'icon-primary',
        label: 'Conclua a aula anterior',
      },
    }),
    [releaseDateText],
  )

  const currentLessonToSet = useMemo(
    () => ({
      ...lessonData.lesson,
      ...dataAvailable,
    }),
    [lessonData.lesson, dataAvailable],
  )

  const lessonStatusStyles = useMemo(() => {
    if (isLessonBlockedByLinearProgress)
      return lessonStatusConfig.linearRequirement
    return isLessonCompleted(lessonData.lesson.id)
      ? lessonStatusConfig.completed
      : lessonStatusConfig.available
  }, [
    lessonStatusConfig,
    isLessonBlockedByLinearProgress,
    isLessonCompleted,
    lessonData.lesson.id,
  ])

  const handlePressLesson = useCallback(() => {
    if (isLessonAvailable === false) return null
    setCurrentLesson(currentLessonToSet)

    // Escolas sem a feature members_page_course_contents não têm a página de
    // consumo no SPA de membros — navegar via router deixaria a URL 404 no
    // refresh. Nesse caso, redireciona (full load) para a página legada do
    // hero_theme (URL sem /online_course).
    if (isWeb && !isPreview && !hasCourseContentsPage) {
      window.location.href = heroThemeCourseContentPath({
        enrollmentId: enrollment.id,
        courseId,
        courseContentId: lessonData.id,
      })
      return
    }

    let formattedPath = courseContentPath({
      isPreview,
      enrollmentId: enrollment.id,
      courseId,
      courseContentId: lessonData.id,
      moduleId,
    })
    if (parentCourseModuleId) {
      formattedPath += `&parentCourseModuleId=${parentCourseModuleId}`
    }
    router.navigate(formattedPath)
  }, [
    isLessonAvailable,
    lessonData.id,
    currentLessonToSet,
    enrollment.id,
    courseId,
    moduleId,
    setCurrentLesson,
    parentCourseModuleId,
    hasCourseContentsPage,
    isPreview,
  ])

  const imageProps = useMemo(() => {
    return {
      style: {
        borderRadius: 8,
      },
      source: {
        uri:
          getVideoThumb({
            ...(lessonData.lesson.media || {
              type: 'Video',
              id: 1,
              title: '',
              duration: 0,
              fileUrl: null,
              fileExt: null,
              text: null,
              hostKey: null,
              host: null,
              organizationUuid: null,
            }),
            coverImage: lessonData.lesson.coverImage,
          }) || undefined,
        height: thumbnailHeight,
      },
    }
  }, [lessonData.lesson.coverImage, lessonData.lesson.media, thumbnailHeight])

  const cardPadding = useMemo(() => {
    if (media.gtLg) return 24
    if (media.gtSm) return 16
    return 12
  }, [media.gtLg, media.gtSm])

  const handleMouseEnter = useCallback(() => {
    if (canAnimateCard) startAnimation(lessonData.id.toString())
  }, [canAnimateCard, startAnimation, lessonData.id])

  const handleMouseLeave = useCallback(() => {
    if (canAnimateCard) endAnimation(lessonData.id.toString())
  }, [canAnimateCard, endAnimation, lessonData.id])

  if (!hasContentLesson) {
    return (
      <EmptyLessonCard
        {...rest}
        cardPadding={cardPadding}
        cardWidthStyle={cardWidthStyle}
        cardMinHeightStyle={cardMinHeightStyle}
      />
    )
  }

  return (
    <Card
      {...rest}
      height="100%"
      flex={1}
      minWidth={cardWidthStyle}
      maxWidth={cardWidthStyle}
      minHeight={cardMinHeightStyle}
      style={{
        cursor: isLessonAvailable ? 'pointer' : 'not-allowed',
      }}
    >
      <Card
        flex={1}
        bg="$bg-surface"
        onPress={handlePressLesson}
        borderRadius={8}
        padding={cardPadding}
        borderColor="transparent"
        borderWidth={2}
        perspective={1000}
        {...(!isWeb && {
          pressStyle: canAnimateCard ? { opacity: 0.7, scale: 0.98 } : {},
          animation: 'quick',
        })}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        scale={1}
        hoverStyle={
          canAnimateCard
            ? {
                borderColor: '$border-hover',
                borderWidth: 2,
                transition: 'all 0.7s ease',
              }
            : {}
        }
      >
        <LessonThumbnail
          scaleValue={scaleValues[lessonData.id] || 1}
          isLessonAvailable={isLessonAvailable}
          displayThumbnail={displayThumbnail}
          imageProps={imageProps}
          lessonTypeContainerHeight={lessonTypeContainerHeight}
          lessonIconColor={lessonIconColor}
          lessonData={lessonData}
        />
        <LessonFooter
          title={lessonData.lesson.title}
          showTitle={showTitle}
          displayLessonStatus={displayLessonStatus}
          lessonStatusStyles={lessonStatusStyles}
          duration={lessonData.lesson.media?.duration}
          examQuestionsCount={lessonData.lesson.activity?.examQuestionsCount}
        />
      </Card>
    </Card>
  )
})
